// components/PerformersManagement.js
import React, { useState, useEffect } from 'react';
import { fetchPerformers, createPerformer, updatePerformer, deletePerformer, uploadPerformerImage } from '../../services/performerService';
import { useAuth } from '../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlus, 
    faPencilAlt, 
    faTrash, 
    faSave, 
    faTimes,
    faUpload,
    faImage,
    faSpinner,
    faArrowsRotate // Use this instead of faSync
} from '@fortawesome/free-solid-svg-icons';

function PerformersManagement({ communityId }) {
  const [performers, setPerformers] = useState([]);
  const [newPerformer, setNewPerformer] = useState({ name: '', description: '' });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [uploadingPerformerId, setUploadingPerformerId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { user } = useAuth();

  const loadPerformers = async () => {
    if (!communityId) {
      setError('No community ID provided');
      return;
    }
    
    setLoading(true);
    try {
      const data = await fetchPerformers(communityId);
      setPerformers(data);
      setError('');
    } catch (err) {
      setError('Failed to load performers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPerformers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleAddPerformer = async () => {
    if (!newPerformer.name.trim()) {
      setError('Izvajalec mora imeti ime');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await createPerformer(communityId, newPerformer);
      setNewPerformer({ name: '', description: '' });
      await loadPerformers();
      setSuccessMessage('Izvajalec je bil uspešno dodan!');
    } catch (err) {
      setError('Failed to create performer');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePerformer = async () => {
    if (!editing || !editing.name.trim()) {
      setError('Izvajalec mora imeti ime');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await updatePerformer(communityId, editing.id, {
        name: editing.name,
        description: editing.description,
        image_url: editing.image_url
      });
      setEditing(null);
      await loadPerformers();
      setSuccessMessage('Izvajalec je bil uspešno posodobljen!');
    } catch (err) {
      setError('Failed to update performer');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePerformer = async (performerId) => {
    if (!window.confirm('Ali ste prepričani, da želite izbrisati tega izvajalca?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await deletePerformer(communityId, performerId);
      await loadPerformers();
      setSuccessMessage('Izvajalec je bil uspešno izbrisan!');
    } catch (err) {
      setError('Failed to delete performer');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, performerId) => {
    if (!file || !performerId) return;

    setImageLoading(true);
    setUploadingPerformerId(performerId);
    setError('');
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await uploadPerformerImage(communityId, performerId, formData);
      
      if (response && response.image_url) {
        // Update in the performers list
        setPerformers(performers.map(performer => 
          performer.id === performerId ? { ...performer, image_url: response.image_url } : performer
        ));
        
        // Also update in editing form if this is the performer being edited
        if (editing && editing.id === performerId) {
          setEditing({ ...editing, image_url: response.image_url });
        }
        
        setSuccessMessage('Slika je bila uspešno naložena!');
      }
    } catch (err) {
      setError(`Failed to upload image: ${err.message || 'Unknown error'}`);
    } finally {
      setImageLoading(false);
      setUploadingPerformerId(null);
    }
  };

  const handleFileChange = (e, performerId) => {
    const file = e.target.files[0];
    if (file && performerId) {
      handleImageUpload(file, performerId);
    }
  };

  const triggerFileInput = (e, performerId) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only allow uploads for existing performers
    if (performerId) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => handleFileChange(e, performerId);
      input.click();
    }
  };

  return (
    <div className="performers-management">
      <h4 className="mb-4">Upravljanje izvajalcev</h4>
      
      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      
      <div className="card mb-4">
        <div className="card-header">Dodaj novega izvajalca</div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Ime</label>
            <input
              type="text"
              className="form-control"
              value={newPerformer.name}
              onChange={(e) => setNewPerformer({...newPerformer, name: e.target.value})}
              placeholder="Vnesi ime izvajalca"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Opis (opcijsko)</label>
            <textarea
              className="form-control"
              value={newPerformer.description}
              onChange={(e) => setNewPerformer({...newPerformer, description: e.target.value})}
              placeholder="Vnesi opis"
              rows={3}
            />
          </div>
          <button 
            className="btn btn-primary" 
            onClick={handleAddPerformer}
            disabled={loading || imageLoading}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Dodajanje...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Dodaj izvajalca
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>Seznam izvajalcev</span>
          {performers.length > 0 && (
            <button 
              className="btn btn-sm btn-outline-primary" 
              onClick={loadPerformers}
              disabled={loading}
            >
              <FontAwesomeIcon icon={loading ? faSpinner : faArrowsRotate} spin={loading} className="me-1" />
              Osveži
            </button>
          )}
        </div>
        <div className="card-body">
          {loading && !performers.length ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Nalaganje...</span>
              </div>
              <p className="mt-2 text-muted">Nalaganje izvajalcev...</p>
            </div>
          ) : performers.length === 0 ? (
            <p className="text-muted text-center py-4">Ni še dodanih izvajalcev</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Ime</th>
                    <th>Opis</th>
                    <th>Slika</th>
                    <th>Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {performers.map(performer => (
                    <tr key={performer.id}>
                      <td>
                        {editing && editing.id === performer.id ? (
                          <input
                            type="text"
                            className="form-control"
                            value={editing.name}
                            onChange={(e) => setEditing({...editing, name: e.target.value})}
                          />
                        ) : (
                          performer.name
                        )}
                      </td>
                      <td className="text-wrap" style={{ maxWidth: '250px' }}>
                        {editing && editing.id === performer.id ? (
                          <textarea
                            className="form-control"
                            value={editing.description || ''}
                            onChange={(e) => setEditing({...editing, description: e.target.value})}
                            rows={2}
                          />
                        ) : (
                          performer.description || '-'
                        )}
                      </td>
                      <td style={{ width: '120px' }}>
                        {editing && editing.id === performer.id ? (
                          <div>
                            <button 
                              className="btn btn-outline-secondary btn-sm" 
                              onClick={(e) => triggerFileInput(e, performer.id)}
                              disabled={imageLoading}
                            >
                              {imageLoading && uploadingPerformerId === performer.id ? (
                                <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                              ) : (
                                <FontAwesomeIcon icon={faUpload} className="me-1" />
                              )}
                              {performer.image_url ? 'Spremeni' : 'Dodaj'}
                            </button>
                            {editing.image_url && (
                              <div className="mt-2">
                                <img 
                                  src={editing.image_url} 
                                  alt="Preview" 
                                  className="img-thumbnail"
                                  style={{ maxHeight: '50px', maxWidth: '100px' }}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            {performer.image_url ? (
                              <div className="position-relative">
                                <img 
                                  src={performer.image_url} 
                                  alt={performer.name}
                                  className="img-thumbnail"
                                  style={{ maxHeight: '50px', maxWidth: '100px' }}
                                />
                                <button
                                  className="btn btn-sm btn-outline-secondary position-absolute top-0 end-0 p-0 m-1"
                                  style={{ width: '20px', height: '20px', fontSize: '10px' }}
                                  onClick={(e) => triggerFileInput(e, performer.id)}
                                  title="Spremeni sliko"
                                  disabled={imageLoading}
                                >
                                  {imageLoading && uploadingPerformerId === performer.id ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                  ) : (
                                    <FontAwesomeIcon icon={faUpload} />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={(e) => triggerFileInput(e, performer.id)}
                                title="Dodaj sliko"
                                disabled={imageLoading}
                              >
                                {imageLoading && uploadingPerformerId === performer.id ? (
                                  <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                  <FontAwesomeIcon icon={faImage} />
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {editing && editing.id === performer.id ? (
                          <div className="btn-group">
                            <button 
                              className="btn btn-success btn-sm"
                              onClick={handleUpdatePerformer}
                              title="Shrani"
                              disabled={loading || imageLoading}
                            >
                              {loading ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                              ) : (
                                <FontAwesomeIcon icon={faSave} />
                              )}
                            </button>
                            <button 
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditing(null)}
                              title="Prekliči"
                              disabled={loading || imageLoading}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        ) : (
                          <div className="btn-group">
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => setEditing({...performer})}
                              title="Uredi"
                              disabled={loading}
                            >
                              <FontAwesomeIcon icon={faPencilAlt} />
                            </button>
                            <button 
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeletePerformer(performer.id)}
                              title="Izbriši"
                              disabled={loading}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PerformersManagement;